"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
// Importamos iconos para darle un toque más moderno y visual
import { Target, Lightbulb } from "lucide-react";
export default function AboutUsSection() {
  // --- HOOKS PARA LA SECCIÓN "OUR STORY" ---
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });

  // --- HOOKS PARA LA SECCIÓN "MISSION & VISION" ---
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <>
      {/* =========================================
          SECCIÓN 1: OUR STORY
       ========================================= */}
      <section className="relative w-full min-h-screen overflow-hidden bg-background">
        {/* IMAGEN DE FONDO */}
        <div className="fixed inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1521681/img/blueprint_pipelines_illustration.png)] bg-cover bg-center opacity-10 pointer-events-none" aria-hidden="true" />

        {/* CONTENIDO PRINCIPAL */}
        <div className="relative z-10 container mx-auto px-6 py-32 md:py-40 lg:py-48 flex items-center justify-center min-h-screen">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background/80 backdrop-blur-md border border-border rounded-lg shadow-medium p-8 md:p-12 lg:p-16 hover:shadow-strong transition-all duration-[var(--duration-medium)] hover:scale-[1.01]">
              <div className="text-center">
                
                <h1 ref={headlineRef} className={`font-display text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-8 tracking-tight transition-all duration-[350ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} hover:-translate-y-0.5 motion-reduce:hover:translate-y-0`}>
                  The Story Behind AA Eagle Plumbing
                </h1>

                <p ref={paragraphRef} className={`font-sans text-lg md:text-xl lg:text-2xl text-muted leading-[1.7] tracking-wide transition-all duration-[350ms] delay-100 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${paragraphVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} hover:-translate-y-0.5 motion-reduce:hover:translate-y-0`}>
                  For 20 years, AA Eagle Plumbing has specialized in delivering fast, reliable, and high-quality water pump services to our valued customers. Our expert technicians handle all types of water pump systems with precision and care, from installation and maintenance to repairs and replacements. We proudly serve both commercial and residential clients, ensuring dependable water pump solutions for every project. Our commitment to timely workmanship and free shop estimates makes us the trusted choice for all your water pump service needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          SECCIÓN 2: MISSION & VISION (MEJORADA)
       ========================================= */}
      <section id="missionvision" className="relative bg-dark-background py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          
          {/* TÍTULO INSPIRADOR PARA LA SECCIÓN EN VEZ DE REPETIR "MISSION & VISION" */}
          <div className="text-center mb-16">
            <h2 className="text-sm md:text-base font-semibold tracking-wider text-secondary uppercase mb-3 drop-shadow-sm">
              Our Core Purpose
            </h2>
            <h3 className="text-4xl md:text-5xl font-display font-bold text-white drop-shadow-sm">
              Driven by Excellence & Trust
            </h3>
          </div>

          <div ref={sectionRef} className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 transition-all duration-[var(--duration-slow)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {/* TARJETA DE LA MISIÓN */}
            <Card className="bg-primary-dark/40 border border-border-strong/30 shadow-medium hover:shadow-strong backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 group overflow-hidden relative">
              {/* Decoración de fondo suave */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -mr-10 -mt-10 transition-transform group-hover:scale-150 duration-700"></div>
              
              <CardHeader className="relative z-10 pb-4">
                <div className="w-14 h-14 rounded-lg bg-primary/20 flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-primary" />
                </div>
                <CardTitle className="text-3xl font-display text-white drop-shadow-sm">
                  Our Mission
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-white/80 leading-relaxed drop-shadow-sm text-lg">
                  To deliver fast, reliable, and high-quality water pump services to both commercial and residential customers. We are committed to providing expert solutions for complex pump systems including fire pumps, boiler feed pumps, chill water pumps, cooling towers, booster pumps, sewage lift stations, line-shaft turbines, and submersible pumps, ensuring dependable performance and customer satisfaction with every project.
                </p>
              </CardContent>
            </Card>

            {/* TARJETA DE LA VISIÓN */}
            <Card className="bg-primary-dark/40 border border-border-strong/30 shadow-medium hover:shadow-strong backdrop-blur-sm transition-all duration-300 hover:-translate-y-2 group overflow-hidden relative">
              {/* Decoración de fondo suave */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-secondary/10 rounded-full blur-3xl -ml-10 -mt-10 transition-transform group-hover:scale-150 duration-700"></div>
              
              <CardHeader className="relative z-10 pb-4">
                <div className="w-14 h-14 rounded-lg bg-secondary/20 flex items-center justify-center mb-6">
                  <Lightbulb className="w-8 h-8 text-secondary" />
                </div>
                <CardTitle className="text-3xl font-display text-white drop-shadow-sm">
                  Our Vision
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <p className="text-white/80 leading-relaxed drop-shadow-sm text-lg">
                  To be the most trusted name in water pump services, recognized for our technical expertise, timely workmanship, and unwavering dedication to quality. We strive to set the standard for excellence in water pump system maintenance and repair, empowering our clients with solutions that ensure safety, efficiency, and long-term reliability.
                </p>
              </CardContent>
            </Card>
          </div>

        </div>
      </section>
    </>;
}